import Vue from 'vue'
import Router from 'vue-router'
import api from './api'
import store from './store'
import User from './views/User.vue'
import UserLogin from './views/user/Login.vue'
import UserRegister from './views/user/Register.vue'
import UserRegisterStart from './views/user/RegisterStart.vue'
import UserRegisterConfirm from './views/user/RegisterConfirm.vue'
import UserRecovery from './views/user/Recovery.vue'
import UserRecoveryStart from './views/user/RecoveryStart.vue'
import UserRecoveryConfirm from './views/user/RecoveryConfirm.vue'
import UserRecoveryEnd from './views/user/RecoveryEnd.vue'
import UserJoin from './views/user/Join.vue'
import Dashboard from './views/Dashboard.vue'
import Project from './views/Project.vue'
import Host from './views/Host.vue'
import Service from './views/Service.vue'
import NewService from './views/NewService.vue'
import NewStack from './views/NewStack.vue'
import Account from './views/Account.vue'
import AccountAccounts from './views/account/Accounts.vue'
import AccountMembers from './views/account/Members.vue'
import AccountRoles from './views/account/Roles.vue'
import AccountKeys from './views/account/Keys.vue'
import AccountProviders from './views/account/Providers.vue'
import Settings from './views/Settings.vue'
import SettingsGeneral from './views/settings/General.vue'
import SettingsPlans from './views/settings/Plans.vue'
import SettingsPayments from './views/settings/Payments.vue'
import SettingsSecurity from './views/settings/Security.vue'
import Error502 from './views/errors/Error502.vue'
import Error404 from './views/errors/Error404.vue'
import ErrorNotExist from './views/errors/NotExist.vue'
import ErrorMaintenance from './views/errors/Maintenance.vue'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/user/:action?',
      component: User,
      meta: {
        requiresAuth: false,
      },
      beforeEnter: (to, from, next) => {
        if (to.query.invitationId) {
          store.dispatch('logout')
        }
        if (store.state.token && !to.query.invitationId) {
          next({path: store.state.config.homePath})
        } else {
          next()
        }
      },
      children: [
        {path: 'login', component: UserLogin},
        {path: 'register', component: UserRegister},
        {path: 'registerStart', component: UserRegisterStart},
        {path: 'registerConfirm', component: UserRegisterConfirm},
        {path: 'recovery', component: UserRecovery},
        {path: 'recoveryStart', component: UserRecoveryStart},
        {path: 'recoveryConfirm', component: UserRecoveryConfirm},
        {path: 'recoveryEnd', component: UserRecoveryEnd},
        {path: 'join', component: UserJoin},
        {path: '*', redirect: '/user/login'},
      ],
    },
    {
      path: '/dashboard',
      component: Dashboard,
      meta: {
        requiresAuth: true,
        requiresData: true,
        requiresSubscription: true,
      },
    },
    {
      path: '/account',
      component: Account,
      meta: {
        requiresAuth: true,
        requiresData: true,
      },
      children: [
        {path: 'accounts', component: AccountAccounts},
        {path: 'members', component: AccountMembers},
        {path: 'roles', component: AccountRoles},
        {path: 'providers', component: AccountProviders},
        {path: 'keys', component: AccountKeys},
        {path: '', redirect: '/account/accounts'},
        {path: 'login', redirect: '/user/login'},
        {path: 'signin', redirect: '/user/login'},
        {path: 'signup', redirect: '/user/register'},
        {path: 'register', redirect: '/user/register'},
        {path: 'registerStart', redirect: '/user/registerStart'},
        {path: 'registerConfirm', redirect: '/user/registerConfirm'},
        {path: 'recovery', redirect: '/user/recovery'},
        {path: 'recoveryStart', redirect: '/user/recoveryStart'},
        {path: 'recoveryConfirm', redirect: '/user/recoveryConfirm'},
        {path: 'recoveryEnd', redirect: '/user/recoveryEnd'},
        {path: 'join', redirect: '/user/join'},
      ],
    },
    {
      path: '/settings',
      component: Settings,
      meta: {
        requiresAuth: true,
        requiresData: true,
      },
      children: [
        {path: 'general', component: SettingsGeneral},
        {path: 'plans', component: SettingsPlans},
        {path: 'payments', component: SettingsPayments},
        {path: 'security', component: SettingsSecurity},
        {path: '', redirect: '/settings/general'},
      ],
    },
    {
      path: '/new-stack',
      component: NewStack,
      meta: {
        requiresAuth: true,
        requiresData: true,
        requiresSubscription: true,
      },
    },
    {
      path: '/new-service',
      component: NewService,
      meta: {
        requiresAuth: true,
        requiresData: true,
        requiresSubscription: true,
      },
    },
    {
      path: '/projects/:project',
      component: Project,
      meta: {
        requiresAuth: true,
        requiresData: true,
        requiresSubscription: true,
      },
    },
    {
      path: '/hosts/:host',
      component: Host,
      meta: {
        requiresAuth: true,
        requiresData: true,
        requiresSubscription: true,
      },
    },
    {
      path: '/services/:service',
      component: Service,
      meta: {
        requiresAuth: true,
        requiresData: true,
        requiresSubscription: true,
      },
    },
    {
      path: '/',
      redirect: '/dashboard',
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/error/502',
      component: Error502,
    },
    {
      path: '/error/not-exist',
      component: ErrorNotExist,
    },
    {
      path: '/error/maintenance',
      component: ErrorMaintenance,
    },
    {
      path: '*',
      component: Error404,
    },
  ],
  scrollBehavior() {
    return {x: 0, y: 0}
  },
})

router.beforeEach((to, from, next) => {
  if (store.getters.config.maintenance) {
    if (to.path === '/error/maintenance') {
      next()
    } else {
      next({
        path: '/error/maintenance',
        query: { redirect: to.path },
      })
    }
    return false
  }
  if (to.query.token) {
    store.dispatch('logout')
    store.dispatch('updateSession', {tracking: false})
    store.dispatch('login', to.query.token)
    next()
    return
  }
  if (!store.state.token && to.matched.some(record => record.meta.requiresAuth)) {
    next({
      path: '/user/login',
      query: { redirect: to.path }
    })
    return
  }

  if (!store.state.connected && to.matched.some(record => record.meta.requiresData)) {
    api.connect().then(next)
    return
  }

  const requiresSubscription = to.matched.some(record => record.meta.requiresSubscription)
  const isActiveSubscriber = store.getters.account._isActiveSubscriber
  const isFreeAccount = store.getters.account._isFree
  if (requiresSubscription && !isActiveSubscriber && !isFreeAccount) {
    next({
      path: '/settings/plans',
      query: { redirect: to.path }
    })
    return
  }

  next()
})

export default router
